import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useAlert } from '../context/AlertContext';
import {
  useGetOpenFormulareStatus,
  useGetRatingForms,
  useGetRatingTriggerByTransaction,
  useGetRatingTriggerEffective,
  useSetRatingTriggerByTransaction,
} from '../hooks/reactQuerys/RatingFormQueries';
import { useGetLegalTransactionStates } from '../hooks/reactQuerys/LegalTransactionQueries';
import {
  ITriggerValue,
  RatingFormTriggerEditor,
} from '../Admin/RatingForm/RatingFormTriggerEditor';
import { ILegalPhaseFullObject } from '../Interfaces/ILegalPhase';
import { RatingActionPanel } from './RatingActionPanel';

interface IProps {
  idLegalTransaction: number;
  idLegalTransactionState?: number | null;
  closedAt?: string | null;
  legalPhases: ILegalPhaseFullObject[];
}

/**
 * Vorgangs-Tab „Bewertungsbögen".
 *
 * Bewusst einfach gehalten und komplett vorgangszentriert — ohne jede
 * Verknüpfung zur Rechtsgeschäftsart oder zur Abwicklung:
 *   1. Bogen-Auswahl (Default = globaler Standardbewertungsbogen)
 *   2. Versandzeitpunkt (Default = beim Abschluss des Vorgangs, State 100)
 *   3. Maske zum Durchführen (Versand an Mandanten + Antworten-Übersicht)
 */
export const RatingFormTab: React.FC<IProps> = ({
  idLegalTransaction,
  idLegalTransactionState,
  closedAt,
  legalPhases,
}) => {
  const { showAlert } = useAlert();
  const { ratingForms } = useGetRatingForms();
  const { legalTransactionStates } = useGetLegalTransactionStates();
  const { ofStatus } = useGetOpenFormulareStatus();
  const ofReady = ofStatus?.isReady ?? false;

  const {
    triggerByTransaction,
    isLoadingTriggerByTransaction,
    refetchTriggerByTransaction,
  } = useGetRatingTriggerByTransaction(idLegalTransaction);
  const {
    triggerEffective,
    isLoadingTriggerEffective,
    refetchTriggerEffective,
  } = useGetRatingTriggerEffective(idLegalTransaction);
  const { setTriggerByTransaction, isPendingTriggerByTransaction } =
    useSetRatingTriggerByTransaction();

  const [value, setValue] = useState<ITriggerValue>({
    idRatingForm: null,
    TriggerType: 'state',
    idLegalTransactionState: 100,
    idLegalPhase: null,
    idLegalRequirement: null,
  });

  // true, sobald der Vorgang eine eigene Konfiguration gespeichert hat.
  const hasOwnOverride = !!triggerByTransaction?.TriggerType;

  // Vorbelegung: eigene Konfig, sonst der effektive Default (globaler
  // Standardbogen + Versand beim Abschluss).
  useEffect(() => {
    if (triggerByTransaction && triggerByTransaction.TriggerType) {
      setValue({
        idRatingForm: triggerByTransaction.idRatingForm,
        TriggerType:
          (triggerByTransaction.TriggerType as ITriggerValue['TriggerType']) ||
          'state',
        idLegalTransactionState:
          triggerByTransaction.idLegalTransactionState ?? 100,
        idLegalPhase: triggerByTransaction.idLegalPhase,
        idLegalRequirement: triggerByTransaction.idLegalRequirement,
      });
    } else if (triggerEffective) {
      setValue({
        idRatingForm: triggerEffective.idRatingForm,
        TriggerType:
          (triggerEffective.TriggerType as ITriggerValue['TriggerType']) ||
          'state',
        idLegalTransactionState:
          triggerEffective.idLegalTransactionState ?? 100,
        idLegalPhase: triggerEffective.idLegalPhase ?? null,
        idLegalRequirement: triggerEffective.idLegalRequirement ?? null,
      });
    }
  }, [triggerByTransaction, triggerEffective]);

  const phaseOptions = useMemo(
    () => legalPhases.map(p => ({ id: p.idLegalPhase, label: p.LegalPhase })),
    [legalPhases]
  );
  const requirementOptions = useMemo(
    () =>
      legalPhases.flatMap(p =>
        (p.LegalRequirementArray ?? []).map(r => ({
          id: r.idLegalRequirement,
          label: `${p.LegalPhase} → ${r.LegalRequirement}`,
        }))
      ),
    [legalPhases]
  );
  const requirementGroups = useMemo(
    () =>
      legalPhases.map(p => ({
        phaseLabel: p.LegalPhase,
        requirements: (p.LegalRequirementArray ?? []).map(r => ({
          id: r.idLegalRequirement,
          label: r.LegalRequirement,
        })),
      })),
    [legalPhases]
  );

  const buildTriggerBody = (): Record<string, unknown> => {
    const body: Record<string, unknown> = { TriggerType: value.TriggerType };
    if (value.idRatingForm != null) body.idRatingForm = value.idRatingForm;
    if (
      value.TriggerType === 'state' &&
      value.idLegalTransactionState != null
    ) {
      body.idLegalTransactionState = value.idLegalTransactionState;
    }
    if (value.TriggerType === 'phase' && value.idLegalPhase != null) {
      body.idLegalPhase = value.idLegalPhase;
    }
    if (
      value.TriggerType === 'requirement' &&
      value.idLegalRequirement != null
    ) {
      body.idLegalRequirement = value.idLegalRequirement;
    }
    return body;
  };

  const handleSave = async () => {
    try {
      await setTriggerByTransaction({
        idLegalTransaction,
        body: buildTriggerBody(),
      });
      await refetchTriggerByTransaction();
      await refetchTriggerEffective();
      showAlert({
        text: 'Bewertungsbogen-Einstellungen gespeichert.',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      showAlert({ text: 'Speichern fehlgeschlagen.', severity: 'error' });
    }
  };

  if (
    isLoadingTriggerByTransaction ||
    isLoadingTriggerEffective ||
    !ratingForms ||
    !legalTransactionStates
  ) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant='body2' color='text.secondary'>
        Standardmäßig ist der globale Standardbewertungsbogen ausgewählt und
        wird beim Abschluss des Vorgangs an die Mandanten versendet. Sie können
        für diesen Vorgang einen anderen Bogen wählen und den Versandzeitpunkt
        anpassen.
      </Typography>

      {!ofReady && (
        <Alert severity='warning'>
          Die Erweiterung „OpenFormulare" ist nicht aktiviert oder nicht
          vollständig konfiguriert. Die Konfiguration ist deaktiviert, bis die
          Erweiterung unter Einstellungen → Erweiterungen aktiviert wurde.
        </Alert>
      )}

      <Paper variant='outlined' sx={{ p: 2, opacity: ofReady ? 1 : 0.5 }}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Bewertungsbogen &amp; Versandzeitpunkt
          {!hasOwnOverride && (
            <Typography
              component='span'
              variant='caption'
              color='text.secondary'
              sx={{ ml: 1 }}
            >
              (Standardvorgabe)
            </Typography>
          )}
        </Typography>
        <RatingFormTriggerEditor
          value={value}
          onChange={setValue}
          ratingForms={ratingForms}
          legalTransactionStates={legalTransactionStates}
          phases={phaseOptions}
          requirements={requirementOptions}
          requirementGroups={requirementGroups}
          disabled={!ofReady}
          showFormSelect
        />
        <Stack direction='row' justifyContent='flex-end' sx={{ mt: 2 }}>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={!ofReady || isPendingTriggerByTransaction}
          >
            Speichern
          </Button>
        </Stack>
      </Paper>

      <Paper variant='outlined' sx={{ p: 2 }}>
        <RatingActionPanel
          idLegalTransaction={idLegalTransaction}
          idLegalTransactionState={idLegalTransactionState}
          closedAt={closedAt}
        />
      </Paper>
    </Stack>
  );
};
